import _createClass from "@babel/runtime/helpers/createClass";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
/**
 * Primitive values which are serializable over JavaScript contexts.
 */

/**
 * Object containing serializable properties.
 * @see {@link SerializablePrimitive} for primitive values.
 */

/**
 * Array containing serializable properties.
 * @see {@link SerializablePrimitive} for primitive values.
 */

/**
 * Serializable values which can be sent
 * @see {@link SerializablePrimitive} for primitive values.
 */

/**
 * Base properties which all commands must inherit.
 *
 * @description Note: All commands currently include a mandatory {@link senderId} and {@link receiverId}. This is
 * to force producers to be explicit in who they are, and who they are trying to communicate with (who = a client).
 *
 * @description All properties MUST be serializable in their representation. The properties defined here may be sent
 * over-the-wire between frame contexts (e.g. consider cross-origin `iframe`s). See {@link SerializableValue}.
 */

/**
 * The base response type expected when handling a given command. Must be serializable over JavaScript contexts.
 */

/**
 * Base container for a command, providing a named reference to a command being invoked by a bridge API client.
 *
 * @description A command in itself does not contain the properties it is being invoked with. Think of it as a symbol
 * which is invoked along with a separate {@link properties} shape, indicating its parameters.
 *
 * @description Commands are serialized over-the-wire into their class name. Properties are serialized into a set of
 * {@link SerializableValue}s, so that a JS context on the other side can deserialize and handle them.
 */
export var Command = /*#__PURE__*/_createClass(
/**
 * A unique, human readable identifier for the command. Differs from {@link Command.name}, which is the JavaScript
 * -defined name given to a function symbol DO NOT use {@link Command.name} for any in-production purposes. It can
 * (1) be collapsed into clashing, minified names in the same bundle, or (2) differ across bundles due to differences
 * in decisions made by JavaScript bundlers in how they minify the name of a symbol.
 */

function Command(properties, result) {
  _classCallCheck(this, Command);
  this.properties = properties;
  this.result = result;
});

/**
 * Type helper for a command constructor.
 */

/**
 * Marker interface for broadcastable commands.
 *
 * @description Broadcastable commands can be used with the broadcast API. They are regular
 * commands with a marker property to assist with type-level constraints, without changing
 * their runtime behaviour.
 */

/**
 * Type helper for a command handler.
 */

/**
 * Type helper for a command with a known sender.
 */

/**
 * Represents the properties of a broadcast command by omitting the `receiverId` property
 * from the given command properties type `C`.
 *
 * @typeParam C - The base command properties type, which must include a `receiverId` property.
 * @remarks
 * This utility type is useful when defining commands that are intended to be broadcast
 * to multiple receivers, where specifying a single `receiverId` is not applicable.
 */

/**
 * Options for broadcasting a command to multiple clients.
 *
 * @property filter - An optional function that determines whether a client,
 *   identified by its `clientId`, should receive the broadcasted command.
 *   If provided, only clients for which this function returns `true` will receive the command.
 */

/**
 * Utility type to extract the CommandProperties type from a CommandConstructor.
 */

/**
 * Utility type to extract the CommandResult type from a CommandConstructor.
 */